import { inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  initialLocalStorageState,
  isLocalStorageKey,
  LOCAL_STORAGE_KEYS_TOKEN,
  LocalStorageState,
} from '../models/local-storage-store.models';

const LEGACY_THEME_KEY = 'uiTheme';

interface LocalStorageRecord {
  key: string;
  value: string | null;
}

export const LocalStorageStore = signalStore(
  { providedIn: 'root' },
  withState(initialLocalStorageState),
  withMethods(store => {
    const localStorageKeys = inject(LOCAL_STORAGE_KEYS_TOKEN);
    const isKey = isLocalStorageKey(localStorageKeys);

    const applyRecords = (records: LocalStorageRecord[]): void => {
      if (records.length === 0) {
        return;
      }
      patchState(store, {
        ...(Object.fromEntries(
          records.map(({ key, value }) => [key, value])
        ) as Partial<LocalStorageState>),
        isInitialized: true,
      });
    };

    const withAccessGuard = (operation: () => void): void => {
      try {
        operation();
      } catch (error) {
        patchState(store, { error: error as Error });
      }
    };

    return {
      refresh(): void {
        withAccessGuard(() =>
          applyRecords(
            localStorageKeys.map(key => ({
              key,
              value: localStorage.getItem(key),
            }))
          )
        );
      },
      refreshByKey(key: string): void {
        if (!isKey(key)) {
          return;
        }
        withAccessGuard(() =>
          applyRecords([{ key, value: localStorage.getItem(key) }])
        );
      },
      saveByKey(key: string, value: string): void {
        if (!isKey(key)) {
          return;
        }
        withAccessGuard(() => {
          localStorage.setItem(key, value);
          applyRecords([{ key, value }]);
        });
      },
      deleteByKey(key: string): void {
        if (!isKey(key)) {
          return;
        }
        withAccessGuard(() => {
          localStorage.removeItem(key);
          patchState(store, { [key]: null } as Partial<LocalStorageState>);
        });
      },
    };
  }),
  withHooks(store => ({
    onInit() {
      try {
        localStorage.removeItem(LEGACY_THEME_KEY);
      } catch (error) {
        patchState(store, { error: error as Error });
      }
    },
  }))
);
